// Input Validation Utilities
// XSS, SQL Injection ve diğer güvenlik tehditlerine karşı koruma

/**
 * Tehlikeli karakterleri temizler (XSS koruması)
 * @param {string} input - Temizlenecek metin
 * @returns {string} Temizlenmiş metin
 */
export const sanitizeInput = (input) => {
  if (typeof input !== 'string') return input;
  
  return input
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};

/**
 * HTML/Script etiketlerini kaldırır
 * @param {string} input - Temizlenecek metin
 * @returns {string} Temizlenmiş metin
 */
export const stripHtml = (input) => {
  if (typeof input !== 'string') return input;
  
  return input.replace(/<[^>]*>/g, '');
};

/**
 * SQL Injection'a karşı tehlikeli karakterleri kaldırır
 * @param {string} input - Kontrol edilecek metin
 * @returns {string} Temizlenmiş metin
 */
export const sanitizeSql = (input) => {
  if (typeof input !== 'string') return input;
  
  // Tehlikeli SQL karakterlerini escape et
  const dangerous = ['--', ';--', ';', '/*', '*/', 'xp_', 'sp_', '0x'];
  let cleaned = input;
  
  dangerous.forEach(char => {
    cleaned = cleaned.replace(new RegExp(char, 'gi'), '');
  });
  
  return cleaned;
};

/**
 * Email formatını doğrular
 * @param {string} email - Kontrol edilecek email
 * @returns {boolean} Geçerli mi?
 */
export const validateEmail = (email) => {
  if (!email || typeof email !== 'string') return false;
  
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email.trim());
};

/**
 * Türkiye telefon numarası formatını doğrular
 * @param {string} phone - Kontrol edilecek telefon
 * @returns {boolean} Geçerli mi?
 */
export const validatePhone = (phone) => {
  if (!phone || typeof phone !== 'string') return false;
  
  // Sadece rakamları al
  const cleaned = phone.replace(/\D/g, '');
  
  // 10 veya 11 haneli olmalı (05xxxxxxxxx veya 5xxxxxxxxx)
  if (cleaned.length === 10 && cleaned.startsWith('5')) return true;
  if (cleaned.length === 11 && cleaned.startsWith('05')) return true;
  
  return false;
};

/**
 * URL formatını doğrular
 * @param {string} url - Kontrol edilecek URL
 * @returns {boolean} Geçerli mi?
 */
export const validateUrl = (url) => {
  if (!url || typeof url !== 'string') return false;
  
  try {
    new URL(url);
    return true;
  } catch {
    return false;
  }
};

/**
 * Karakter uzunluğunu kontrol eder
 * @param {string} input - Kontrol edilecek metin
 * @param {number} min - Minimum uzunluk
 * @param {number} max - Maximum uzunluk
 * @returns {object} { valid: boolean, message: string }
 */
export const validateLength = (input, min = 0, max = Infinity) => {
  if (typeof input !== 'string') {
    return { valid: false, message: 'Geçersiz veri tipi' };
  }
  
  const length = input.trim().length;
  
  if (length < min) {
    return { valid: false, message: `En az ${min} karakter olmalıdır` };
  }
  
  if (length > max) {
    return { valid: false, message: `En fazla ${max} karakter olabilir` };
  }
  
  return { valid: true, message: '' };
};

/**
 * Sadece alfanumerik karakterler (Türkçe dahil)
 * @param {string} input - Kontrol edilecek metin
 * @returns {boolean} Geçerli mi?
 */
export const isAlphanumeric = (input) => {
  if (!input || typeof input !== 'string') return false;
  
  // Türkçe karakterler dahil
  const regex = /^[a-zA-ZğüşöçİĞÜŞÖÇ0-9\s]+$/;
  return regex.test(input);
};

/**
 * Sadece rakamlar
 * @param {string} input - Kontrol edilecek metin
 * @returns {boolean} Geçerli mi?
 */
export const isNumeric = (input) => {
  if (!input || typeof input !== 'string') return false;
  
  return /^\d+$/.test(input);
};

/**
 * Fiyat formatını doğrular (pozitif sayı)
 * @param {string|number} price - Kontrol edilecek fiyat
 * @returns {object} { valid: boolean, message: string }
 */
export const validatePrice = (price) => {
  const numPrice = parseFloat(price);
  
  if (isNaN(numPrice)) {
    return { valid: false, message: 'Geçerli bir fiyat giriniz' };
  }
  
  if (numPrice < 0) {
    return { valid: false, message: 'Fiyat negatif olamaz' };
  }
  
  if (numPrice === 0) {
    return { valid: false, message: 'Fiyat 0 olamaz' };
  }
  
  if (numPrice > 1000000) {
    return { valid: false, message: 'Fiyat 1.000.000 TL\'den fazla olamaz' };
  }
  
  return { valid: true, message: '' };
};

/**
 * Dosya boyutunu kontrol eder
 * @param {File} file - Kontrol edilecek dosya
 * @param {number} maxSizeMB - Maximum boyut (MB)
 * @returns {object} { valid: boolean, message: string }
 */
export const validateFileSize = (file, maxSizeMB = 5) => {
  if (!file) {
    return { valid: false, message: 'Dosya bulunamadı' };
  }
  
  const maxSizeBytes = maxSizeMB * 1024 * 1024;
  
  if (file.size > maxSizeBytes) {
    return { valid: false, message: `Dosya boyutu ${maxSizeMB}MB\'dan küçük olmalıdır` };
  }
  
  return { valid: true, message: '' };
};

/**
 * Dosya tipini kontrol eder
 * @param {File} file - Kontrol edilecek dosya
 * @param {string[]} allowedTypes - İzin verilen tipler ['image/jpeg', 'image/png']
 * @returns {object} { valid: boolean, message: string }
 */
export const validateFileType = (file, allowedTypes = []) => {
  if (!file) {
    return { valid: false, message: 'Dosya bulunamadı' };
  }
  
  if (allowedTypes.length === 0) {
    return { valid: true, message: '' };
  }
  
  if (!allowedTypes.includes(file.type)) {
    return { valid: false, message: 'Desteklenmeyen dosya formatı' };
  }
  
  return { valid: true, message: '' };
};

/**
 * Görsel dosyası mı kontrol eder
 * @param {File} file - Kontrol edilecek dosya
 * @returns {object} { valid: boolean, message: string }
 */
export const validateImageFile = (file) => {
  const allowedTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
  return validateFileType(file, allowedTypes);
};

/**
 * 3D model dosyası mı kontrol eder
 * @param {File} file - Kontrol edilecek dosya
 * @returns {object} { valid: boolean, message: string }
 */
export const validate3DFile = (file) => {
  if (!file) {
    return { valid: false, message: 'Dosya bulunamadı' };
  }
  
  const allowedExtensions = ['.stl', '.obj', '.fbx', '.3ds', '.blend', '.max'];
  const fileName = file.name.toLowerCase();
  const hasValidExtension = allowedExtensions.some(ext => fileName.endsWith(ext));
  
  if (!hasValidExtension) {
    return { 
      valid: false, 
      message: 'Sadece STL, OBJ, FBX, 3DS, BLEND, MAX dosyaları yüklenebilir' 
    };
  }
  
  return { valid: true, message: '' };
};

/**
 * Şifre güvenliğini kontrol eder
 * @param {string} password - Kontrol edilecek şifre
 * @returns {object} { valid: boolean, message: string, strength: string }
 */
export const validatePassword = (password) => {
  if (!password || typeof password !== 'string') {
    return { valid: false, message: 'Şifre giriniz', strength: 'none' };
  }
  
  if (password.length < 6) {
    return { valid: false, message: 'Şifre en az 6 karakter olmalıdır', strength: 'weak' };
  }
  
  let strength = 'weak';
  let score = 0;
  
  // Uzunluk kontrolü
  if (password.length >= 8) score++;
  if (password.length >= 12) score++;
  
  // Büyük harf kontrolü
  if (/[A-Z]/.test(password)) score++;
  
  // Küçük harf kontrolü
  if (/[a-z]/.test(password)) score++;
  
  // Rakam kontrolü
  if (/[0-9]/.test(password)) score++;
  
  // Özel karakter kontrolü
  if (/[^A-Za-z0-9]/.test(password)) score++;
  
  if (score <= 2) strength = 'weak';
  else if (score <= 4) strength = 'medium';
  else strength = 'strong';
  
  return { 
    valid: true, 
    message: '', 
    strength,
    score
  };
};

/**
 * Form verilerini toplu olarak doğrular
 * @param {object} data - Form verileri
 * @param {object} rules - Doğrulama kuralları
 * @returns {object} { valid: boolean, errors: object }
 */
export const validateForm = (data, rules) => {
  const errors = {};
  
  Object.keys(rules).forEach(field => {
    const value = data[field];
    const fieldRules = rules[field];
    
    // Required kontrolü
    if (fieldRules.required && (!value || value.toString().trim() === '')) {
      errors[field] = `${fieldRules.label || field} zorunludur`;
      return;
    }
    
    // Eğer value yoksa ve required değilse, diğer kontrolleri atla
    if (!value) return;
    
    // Length kontrolü
    if (fieldRules.minLength || fieldRules.maxLength) {
      const result = validateLength(
        value.toString(), 
        fieldRules.minLength || 0, 
        fieldRules.maxLength || Infinity
      );
      if (!result.valid) {
        errors[field] = result.message;
        return;
      }
    }
    
    // Email kontrolü
    if (fieldRules.email && !validateEmail(value)) {
      errors[field] = 'Geçerli bir email adresi giriniz';
      return;
    }
    
    // Phone kontrolü
    if (fieldRules.phone && !validatePhone(value)) {
      errors[field] = 'Geçerli bir telefon numarası giriniz';
      return;
    }
    
    // Numeric kontrolü
    if (fieldRules.numeric && !isNumeric(value.toString())) {
      errors[field] = 'Sadece rakam girebilirsiniz';
      return;
    }
    
    // Price kontrolü
    if (fieldRules.price) {
      const result = validatePrice(value);
      if (!result.valid) {
        errors[field] = result.message;
        return;
      }
    }
    
    // Custom validation fonksiyonu
    if (fieldRules.custom && typeof fieldRules.custom === 'function') {
      const customResult = fieldRules.custom(value);
      if (!customResult.valid) {
        errors[field] = customResult.message;
        return;
      }
    }
  });
  
  return {
    valid: Object.keys(errors).length === 0,
    errors
  };
};

/**
 * XSS saldırılarına karşı tüm string alanları temizler
 * @param {object} data - Temizlenecek veri objesi
 * @returns {object} Temizlenmiş veri
 */
export const sanitizeObject = (data) => {
  const cleaned = {};
  
  Object.keys(data).forEach(key => {
    const value = data[key];
    
    if (typeof value === 'string') {
      cleaned[key] = stripHtml(value).trim();
    } else if (typeof value === 'object' && value !== null) {
      cleaned[key] = sanitizeObject(value);
    } else {
      cleaned[key] = value;
    }
  });
  
  return cleaned;
};

/**
 * Yaygın kullanım için hazır validasyon kuralları
 */
export const commonRules = {
  email: {
    required: true,
    email: true,
    label: 'Email'
  },
  password: {
    required: true,
    minLength: 6,
    label: 'Şifre'
  },
  phone: {
    phone: true,
    label: 'Telefon'
  },
  name: {
    required: true,
    minLength: 2,
    maxLength: 50,
    label: 'İsim'
  },
  description: {
    maxLength: 1000,
    label: 'Açıklama'
  },
  price: {
    required: true,
    price: true,
    label: 'Fiyat'
  }
};

export default {
  sanitizeInput,
  stripHtml,
  sanitizeSql,
  validateEmail,
  validatePhone,
  validateUrl,
  validateLength,
  isAlphanumeric,
  isNumeric,
  validatePrice,
  validateFileSize,
  validateFileType,
  validateImageFile,
  validate3DFile,
  validatePassword,
  validateForm,
  sanitizeObject,
  commonRules
};

